import React, { useCallback, useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { ReviewCard } from "@/components/ReviewCard";
import { EmptyState } from "@/components/EmptyState";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { fetchLatestReviews, fetchReviews } from "@/lib/api";
import type { Review } from "@/types";

const PAGE = 20;

/** All reviews — for one product (slug) or the whole shop. Loads 20 at a time. */
export function ReviewsPage({ slug }: { slug?: string }) {
  const { t } = useI18n();
  const { goBack } = useRouter();
  const [items, setItems] = useState<Review[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useBackButton(goBack);

  const load = useCallback(
    async (offset: number) => {
      setLoading(true);
      try {
        const res = slug ? await fetchReviews(slug, PAGE, offset) : await fetchLatestReviews(PAGE, offset);
        setItems((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
        setTotal(res.total);
      } catch {
        setTotal((prev) => prev ?? 0);
      } finally {
        setLoading(false);
      }
    },
    [slug],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <>
      <TopBar title={t("reviews.allTitle")} onBack={goBack} />
      <main className="page page--no-nav">
        {total === null ? (
          <p style={{ color: "var(--color-muted)" }}>{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <EmptyState art="reviews" title={t("product.noReviews")} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((r) => (
              <ReviewCard key={r.id} review={r} showProduct={!slug} />
            ))}
            {items.length < total && (
              <button className="btn btn--ghost" style={{ width: "100%" }} disabled={loading} onClick={() => load(items.length)}>
                {loading ? t("common.loading") : t("reviews.showMore")}
              </button>
            )}
          </div>
        )}
      </main>
    </>
  );
}
